<template>
	<div
		class="hidden-horizontal-scrollbar"
		:class="{ 'hidden-horizontal-scrollbar--fill-width': fillWidth }"
		@touchstart.stop
	>
		<div
			class="hidden-horizontal-scrollbar__items"
			:class="{
				'hidden-horizontal-scrollbar__items--faded-end': fadedEdge && !isAtEnd,
				'hidden-horizontal-scrollbar__items--faded-start': fadedEdge && curScrollPos !== 0,
				'hidden-horizontal-scrollbar__items--faded': fadedEdge && !isAtStart && !isAtEnd,
				'snap-container': scrollSnap,
			}"
			ref="scroller"
			@scroll="() => scrolled()"
		>
			<slot></slot>
		</div>

		<ClientOnly>
			<span
				class="hidden-horizontal-scrollbar__nav hidden-horizontal-scrollbar__nav--start"
				v-if="showChevronStart"
				@click.stop.prevent="scrollToEnd"
			>
				<FontAwesomeIcon :icon="faChevronLeft" />
			</span>

			<span
				class="hidden-horizontal-scrollbar__nav hidden-horizontal-scrollbar__nav--end"
				v-if="showChevronEnd"
				@click.stop.prevent="scrollToStart"
			>
				<FontAwesomeIcon :icon="faChevronRight" />
			</span>
		</ClientOnly>
	</div>
</template>

<script setup lang="ts">
import { computed, nextTick, onActivated, onDeactivated, onMounted, onUnmounted, ref, watch } from 'vue';

import { SnowplowContext } from '@/helpers/tracking/providers';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { AppDirection } from '@/enums/app-direction';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { storeToRefs } from 'pinia';
import { useMetaStore } from '@/stores/pinia/useMetaStore';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';

const props = withDefaults(
	defineProps<{
		resetScrollOnChange?: boolean;
		allItemsLoaded?: boolean;
		emitOnScroll?: boolean;
		disabled?: boolean;
		fadedEdge?: boolean;
		hideNav?: boolean;
		scrollSnap?: boolean;
		fillWidth?: boolean;
		additionalContexts?: SnowplowContext[];
	}>(),
	{
		resetScrollOnChange: false,
		allItemsLoaded: true,
		emitOnScroll: false,
		disabled: false,
		fadedEdge: false,
		hideNav: false,
		additionalContexts: () => [],
	}
);

const emits = defineEmits<{
	(e: 'onScrollRight'): void;
	(e: 'scrollbarClicked', label: 'left' | 'right'): void;
}>();

const { activeRoute, lastRoute } = storeToRefs(useRoutingStore());
const { appDirection } = storeToRefs(useMetaStore());

const scroller = ref<Element | null>(null);
const isScrollBarVisible = ref(false);
const curScrollPos = ref(0);
const savedScrollPos = ref(0);
const isAtEnd = ref(false);
const selectedItem = ref(0);

const isAtStart = computed(() => !!(curScrollPos?.value === 0));

const disableChevrons = computed(() => props.disabled || props.hideNav || !isScrollBarVisible.value);
const showChevronEnd = computed(() => (!props.allItemsLoaded || !isAtEnd.value) && !disableChevrons.value);
const showChevronStart = computed(() => !isAtStart.value && !disableChevrons.value);

const activeRouteName = computed(() => activeRoute.value);
watch(activeRouteName, () => {
	if (
		props.resetScrollOnChange &&
		activeRouteName.value?.meta?.tab === 'detail' &&
		lastRoute.value?.params.title !== activeRoute.value?.params.title
	) {
		resetScrollPosition();
	}
});

onMounted(() => nextTick(() => checkScrollable()));

onActivated(() => {
	if (Math.abs(savedScrollPos.value) > 0 && scroller.value?.isConnected) {
		scroller.value?.scrollTo({ top: 0, left: savedScrollPos.value });
		selectedItem.value = 0;
	}
});

onDeactivated(() => {
	if (Math.abs(curScrollPos.value) > 0) {
		savedScrollPos.value = curScrollPos.value;
	}
});

const isRTL = computed(() => appDirection.value === AppDirection.RTL);

function getScrollPosition(scrollLeftAmount: number): number {
	return isRTL.value ? -scrollLeftAmount : scrollLeftAmount;
}

function scrolled() {
	if (!scroller.value || !scroller.value?.isConnected) return;

	curScrollPos.value = scroller.value.scrollLeft;

	// Prevent division by zero
	const maxScroll = scroller.value.scrollWidth - scroller.value.clientWidth;

	// Calculate the scroll progress
	const progress = maxScroll > 0 ? Math.round((Math.abs(scroller.value.scrollLeft) / maxScroll) * 100) : 100;

	if (
		progress < 100 ||
		(scroller.value.scrollWidth === 0 &&
			scroller.value.clientWidth === 0 &&
			scroller.value.clientHeight === 0 &&
			props.allItemsLoaded)
	) {
		isAtEnd.value = false;
	} else {
		isAtEnd.value = true;
	}

	if (progress > 40) {
		emits('onScrollRight');
	}
	if (isAtEnd.value && !props.allItemsLoaded) {
		emits('onScrollRight');
		isAtEnd.value = false;
	}
}

function scrollToEnd() {
	scrollBy(-(scroller.value?.clientWidth! - 90));
	trackScroll('left');
}

function scrollToStart() {
	scrollBy(scroller.value?.clientWidth! - 90);
	trackScroll('right');
}

function scrollBy(n: number | undefined) {
	if (!scroller.value?.isConnected) return;
	scroller.value?.scrollBy({ top: 0, left: getScrollPosition(n!), behavior: 'smooth' });
}

function resetScrollPosition() {
	if (!scroller.value || !scroller.value?.isConnected) return;
	scroller.value.scrollTo({ top: 0, left: 0, behavior: 'auto' });
	selectedItem.value = 0;
}

function trackScroll(label: 'left' | 'right') {
	if (props.emitOnScroll) {
		emits('scrollbarClicked', label);
	} else {
		TrackingHelper.trackEvent(
			'userinteraction',
			{
				action: 'horizontal_scroller_clicked',
				label,
			},
			props.additionalContexts
		);
	}
}

function handleMutations(mutationsList: MutationRecord[]) {
	for (const mutation of mutationsList) {
		if (mutation.type === 'childList') {
			// Check scrollbar visibility when mutations occur
			isScrollBarVisible.value = isHorizontalScrollBarVisible(scroller.value);
		}
	}
}

function isHorizontalScrollBarVisible(element: Element | null) {
	return element !== null && element?.scrollWidth > element?.clientWidth;
}

let observer: MutationObserver | null = null;

// this function checks if the scrollbar is visible
function checkScrollable() {
	isScrollBarVisible.value = isHorizontalScrollBarVisible(scroller.value);

	// Start observing the target element for mutations
	if (scroller.value != null) {
		observer = new MutationObserver(handleMutations);
		observer.observe(scroller.value, { childList: true, subtree: true });
	}
}

onUnmounted(() => {
	observer?.disconnect();
	observer = null;
});

defineExpose({
	scroller,
});
</script>

<style lang="scss">
@import '~@/styles/variables.scss';
@import '~@/styles/mixins.scss';
@import '~@/styles/rtl-support-mixins.scss';

.hidden-horizontal-scrollbar {
	overflow: hidden;
	display: flex;
	position: relative;

	&--fill-width {
		min-width: 100%;

		.hidden-horizontal-scrollbar__items {
			min-width: 100%;
		}
	}

	&__items {
		@include hidden-horizontal-scrollbar();
		position: relative;
		flex-wrap: nowrap;
		display: flex;
		align-items: center;
		flex-grow: 1;

		& > * {
			flex: 0 0 auto;
		}

		&--faded-end {
			mask-image: linear-gradient(to right, $jw-color-darkblue--dark 90%, transparent 100%);
		}

		&--faded-start {
			mask-image: linear-gradient(to right, transparent 0%, $jw-color-darkblue--dark 5%);
		}

		&--faded {
			mask-image: linear-gradient(
				to left,
				transparent 0%,
				$jw-color-darkblue--dark 5%,
				$jw-color-darkblue--dark 95%,
				transparent 100%
			);
		}

		&.snap-container {
			scroll-snap-type: x mandatory;
			scroll-behavior: smooth;
		}
	}

	&__nav {
		background: rgba($jw-color-darkblue--dark, 0.8);
		cursor: pointer;
		position: absolute;
		top: 0;
		height: 100%;
		width: 40px;
		display: none;
		font-size: 25px;
		justify-content: center;
		align-items: center;
		z-index: 30;

		&--end {
			@include inset-inline('end', 0);
		}

		&--start {
			@include inset-inline('start', 0);
		}
	}

	/* no tablet or mobile device */
	@media (pointer: fine) {
		&:hover {
			.hidden-horizontal-scrollbar__nav {
				display: flex !important;
			}
		}
	}

	/* full width style for mobile viewports */
	@media (max-width: $screen-xs-max) {
		@include full-width();

		&__items {
			padding-left: 15px;
			padding-right: 15px;
		}
	}
}
</style>
